import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckIcon, CrownIcon, RocketIcon, BuildingIcon, AlertTriangleIcon, XIcon, ArrowDownIcon, UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { FEATURE_CATALOG, FEATURE_BY_ID } from '../lib/featureCatalog';
import { resolvePlanFeatures } from '../lib/planFeatures';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPlans = () => {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'
  const [activeCampaign, setActiveCampaign] = useState(null);
  // Downgrade preview modal state — user'ın "Hemen Düşür" tıkladığı plana ait
  // dry-run önizleme burada tutulur. `modal` açılırken `loading`, veri gelince
  // `data`, kullanıcı onaylayınca `handleSubscribe` tetiklenir ve modal kapanır.
  const [downgradePreview, setDowngradePreview] = useState({
    open: false,
    loading: false,
    targetSlug: null,
    data: null,
  });
  // Kullanıcı hangi plana tıkladıysa o kart öne çıkar. Varsayılan olarak
  // 'professional' seçili (en popüler kabul) — kullanıcı başka bir plana
  // tıklarsa spotlight ona geçer.
  const [selectedPlanId, setSelectedPlanId] = useState('professional');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const trialExpired = !!user?.trial_info?.is_expired;
  const currentPlanSlug = (user?.plan_slug || '').toLowerCase();
  const paymentStatus = (user?.company?.payment_status || '').toLowerCase();
  const isCancelPending = paymentStatus === 'cancel_pending';

  // Plan hierarchy (lowest → highest). Used to render "Hemen Düşür" vs
  // "Hemen Yükselt" on the CTA button when comparing against the tenant's
  // current plan. Unknown slugs default to rank -1 (below anything).
  const PLAN_RANK = { free_trial: 0, solo: 1, starter: 2, professional: 3, enterprise: 4 };
  const rankOf = (slug) => (PLAN_RANK[String(slug || '').toLowerCase()] ?? -1);
  const currentPlanRank = rankOf(currentPlanSlug);
  const ctaLabelFor = (packageId) => {
    const targetRank = rankOf(packageId);
    if (currentPlanRank < 0 || targetRank < 0) return 'Hemen Başla';
    if (targetRank > currentPlanRank) return 'Hemen Yükselt';
    if (targetRank < currentPlanRank) return 'Hemen Düşür';
    return 'Hemen Başla'; // same rank (should be handled by isCurrent, safety fallback)
  };

  // ============ Active Campaign (Ultra Admin > Kampanyalar) ============
  // Fetch on mount + refresh every 60s so the "Son N saat" countdown ticks.
  useEffect(() => {
    let mounted = true;
    const fetchCampaign = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/active-campaign`);
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) setActiveCampaign(data.campaign || null);
      } catch { /* silent */ }
    };
    fetchCampaign();
    const timer = setInterval(fetchCampaign, 60_000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  // Compute campaign discount for a given plan slug (respecting applicable_packages).
  // Empty applicable_packages list means the campaign is targeted to NO plans
  // (admin must explicitly select which packages it applies to). This prevents
  // accidental "campaign applied to all" if admin forgets to check any package.
  const getCampaignPct = (planSlug) => {
    if (!activeCampaign || !activeCampaign.discount_percentage) return 0;
    const list = activeCampaign.applicable_packages || [];
    if (list.length === 0) return 0;
    const key = String(planSlug || '').toLowerCase();
    if (!key) return 0;
    const applies = list.map(s => String(s).toLowerCase()).includes(key);
    if (!applies) return 0;
    return Math.max(0, Math.min(90, Number(activeCampaign.discount_percentage) || 0));
  };

  // Calculate displayed price + saving for a given package.
  // Applies (1) campaign discount to the monthly base, then (2) yearly discount
  // on top if user is on yearly cycle. Both layers are Ultra Admin-configurable.
  //
  // `packageId` (the key from Object.entries) is the authoritative slug — the
  // packageData object might not carry `slug` directly, so we pass it in.
  const getDisplayPrice = (packageData, packageId) => {
    const slug = packageId || packageData?.slug || packageData?.code;
    const campaignPct = getCampaignPct(slug);
    const baseMonthly = Number(packageData?.price ?? 0);
    const monthlyPrice = +(baseMonthly * (1 - campaignPct / 100)).toFixed(2);
    const rawPct = Number(
      packageData?.yearly_discount_percent
      ?? packageData?.yearlyDiscountPercent
      ?? 20
    );
    const discountPct = Math.max(0, Math.min(90, isFinite(rawPct) ? rawPct : 20));
    if (billingCycle === 'yearly') {
      const fullYear = +(monthlyPrice * 12).toFixed(2);
      const listYear = +(baseMonthly * 12).toFixed(2);
      const yearly = +(fullYear * (1 - discountPct / 100)).toFixed(2);
      const savings = +(listYear - yearly).toFixed(2);
      return {
        price: yearly,
        suffix: '/yıl',
        savings,
        fullYear: listYear,   // crossed-out list price = base * 12
        discountPct,
        campaignPct,
        hasCampaign: campaignPct > 0,
        baseMonthly,
        listYear,
      };
    }
    return {
      price: monthlyPrice,
      suffix: '/ay',
      savings: campaignPct > 0 ? +(baseMonthly - monthlyPrice).toFixed(2) : 0,
      fullYear: 0,
      discountPct,
      campaignPct,
      hasCampaign: campaignPct > 0,
      baseMonthly,
      listYear: 0,
    };
  };

  useEffect(() => {
    fetchPackages();
    checkReturnFromStripe();
  }, []);
  
  // Separate effect for auto-triggering payment after packages load
  useEffect(() => {
    const selectedPlan = searchParams.get('selected_plan');
    if (selectedPlan && ['starter', 'professional', 'enterprise'].includes(selectedPlan) && Object.keys(packages).length > 0) {
      // Wait a bit for UI to render, then trigger payment
      setTimeout(() => {
        handleSubscribe(selectedPlan);
      }, 1500);
    }
  }, [packages]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/packages`);
      const all = response.data.packages || {};
      // Hide the 14-day free trial package - paid plans only on subscription page
      const { free_trial, ...paid } = all;
      // Sipariş: fiyata göre artan sıra (Solo → Enterprise). API dict
      // sırasız gelebiliyor, o yüzden burada sabitliyoruz.
      const PLAN_ORDER = ['solo', 'starter', 'professional', 'enterprise'];
      const ordered = {};
      PLAN_ORDER.forEach((slug) => { if (paid[slug]) ordered[slug] = paid[slug]; });
      // Bilinmeyen ekstra planlar varsa (Ultra Admin custom eklerse) sona at
      Object.keys(paid).forEach((slug) => { if (!ordered[slug]) ordered[slug] = paid[slug]; });
      setPackages(ordered);
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Fallback to static data if API fails
      setPackages({
        solo: {
          name: "Solo Plan",
          price: 15.0,
          currency: "usd",
          user_limit: 1,
          features: ["expense_entry", "personal_expenses", "expense_dashboard"]
        },
        starter: {
          name: "Starter Plan",
          price: 29.0,
          currency: "usd",
          user_limit: 5,
          features: ["Basic advance management", "Basic reporting", "Email support"]
        },
        professional: {
          name: "Professional Plan", 
          price: 39.0,
          currency: "usd",
          user_limit: 15,
          features: ["Advanced reporting", "User permissions", "API access", "Priority support"]
        },
        enterprise: {
          name: "Enterprise Plan",
          price: 49.0,
          currency: "usd", 
          user_limit: 50,
          features: ["Unlimited features", "Custom integrations", "Dedicated support", "SLA guarantee"]
        }
      });
    }
  };

  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const checkReturnFromStripe = () => {
    const sessionId = getUrlParameter('session_id');
    if (sessionId) {
      toast.info('Ödeme durumu kontrol ediliyor...');
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 3000; // 3 seconds

    if (attempts >= maxAttempts) {
      toast.error('Ödeme durumu kontrolü zaman aşımına uğradı. Lütfen destek ile iletişime geçin.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/checkout/status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Ödeme başarılı! Aboneliğiniz aktifleştirildi.');
        // Refresh user data so trial_info.is_expired clears, then go to dashboard
        try {
          if (checkAuth) await checkAuth();
        } catch (e) {
          /* ignore */
        }
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Ödeme oturumu süresi doldu. Lütfen tekrar deneyin.');
        return;
      }

      // Continue polling if payment is still pending
      toast.info('Ödeme işleniyor... ⏳');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);

    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Ödeme durumu kontrolünde hata oluştu.');
    }
  };

  // Downgrade preview handlers — hoisted to component scope so the modal
  // (rendered outside the packages-IIFE) can call closeDowngradePreview.
  const openDowngradePreview = async (targetSlug) => {
    setDowngradePreview({ open: true, loading: true, targetSlug, data: null });
    try {
      const res = await axios.get(`${BACKEND_URL}/api/company/downgrade-preview/${targetSlug}`);
      setDowngradePreview({ open: true, loading: false, targetSlug, data: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Downgrade önizlemesi alınamadı');
      setDowngradePreview({ open: false, loading: false, targetSlug: null, data: null });
    }
  };
  const closeDowngradePreview = () =>
    setDowngradePreview({ open: false, loading: false, targetSlug: null, data: null });

  const handleSubscribe = async (packageId, useMockup = false) => {
    setLoadingPackage(packageId + (useMockup ? '-mockup' : ''));
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Lütfen önce giriş yapın');
        navigate('/login');
        return;
      }

      // Get current URL for success and cancel URLs
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = currentUrl;

      const requestBody = {
        package_id: packageId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mockup: useMockup,
        billing_cycle: billingCycle,
        metadata: {
          source: 'subscription_page',
          package_name: packages[packageId]?.name,
          mockup: useMockup ? 'true' : 'false',
          billing_cycle: billingCycle
        }
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout/session`,
        requestBody,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.checkout_url) {
        const url = response.data.checkout_url;
        // Mockup endpoint returns a relative path; prefix with backend URL
        const finalUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
        // Redirect to checkout (Stripe or Mockup)
        window.location.href = finalUrl;
      } else {
        throw new Error('Ödeme URL\'si alınamadı');
      }

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Ödeme başlatılamadı. Lütfen tekrar deneyin.');
      setLoading(false);
      setLoadingPackage(null);
    }
  };

  const getPackageIcon = (packageId) => {
    switch (packageId) {
      case 'starter':
        return <RocketIcon className="w-8 h-8 text-blue-600" />;
      case 'professional':
        return <CrownIcon className="w-8 h-8 text-purple-600" />;
      case 'enterprise':
        return <BuildingIcon className="w-8 h-8 text-indigo-600" />;
      default:
        return <RocketIcon className="w-8 h-8 text-gray-600" />;
    }
  };

  const getPackageColor = (packageId) => {
    switch (packageId) {
      case 'starter':
        return 'border-blue-200 hover:border-blue-300';
      case 'professional':
        return 'border-purple-200 hover:border-purple-300 ring-2 ring-purple-100';
      case 'enterprise':
        return 'border-indigo-200 hover:border-indigo-300';
      default:
        return 'border-gray-200 hover:border-gray-300';
    }
  };

  const getButtonColor = (packageId) => {
    switch (packageId) {
      case 'starter':
        return 'bg-blue-600 hover:bg-blue-700';
      case 'professional':
        return 'bg-purple-600 hover:bg-purple-700';
      case 'enterprise':
        return 'bg-indigo-600 hover:bg-indigo-700';
      default:
        return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Active Campaign Banner — Ultra Admin > Kampanyalar.
            Escalates urgency based on time remaining:
              > 3 days   → orange           (gentle nudge)
              1-3 days   → red              (pressing)
              < 24 hours → red + pulse      (final push, "Son X saat!")
              < 1 hour   → red + pulse + big (last chance) */}
        {activeCampaign && activeCampaign.discount_percentage > 0
          && Array.isArray(activeCampaign.applicable_packages)
          && activeCampaign.applicable_packages.length > 0
          && (() => {
          const secs = Number(activeCampaign.seconds_remaining ?? 0);
          const days = Number(activeCampaign.days_remaining ?? 0);
          // Tier & label
          let tier = 'safe';
          let timeLabel;
          if (secs !== null && secs > 0) {
            if (secs < 3600) {
              tier = 'final';
              const mins = Math.max(1, Math.floor(secs / 60));
              timeLabel = `Son ${mins} dakika!`;
            } else if (secs < 86400) {
              tier = 'critical';
              const hours = Math.max(1, Math.floor(secs / 3600));
              timeLabel = `Son ${hours} saat!`;
            } else if (days <= 3) {
              tier = 'urgent';
              timeLabel = `${days} gün kaldı`;
            } else {
              tier = 'safe';
              timeLabel = `${days} gün kaldı`;
            }
          } else if (secs === 0 && activeCampaign.end_date) {
            // seconds_remaining is 0 → campaign about to expire
            tier = 'final';
            timeLabel = 'Son saatler!';
          }

          const styles = {
            safe:     { box: 'border-orange-400 from-orange-50 via-amber-50 to-orange-50',
                        chip: 'bg-orange-500',
                        pulse: '' },
            urgent:   { box: 'border-red-400 from-red-50 via-orange-50 to-red-50',
                        chip: 'bg-red-500',
                        pulse: '' },
            critical: { box: 'border-red-500 from-red-100 via-red-50 to-red-100',
                        chip: 'bg-red-600',
                        pulse: 'animate-pulse' },
            final:    { box: 'border-red-600 from-red-100 via-red-50 to-red-100 shadow-md',
                        chip: 'bg-red-700 text-base',
                        pulse: 'animate-pulse ring-2 ring-red-400 ring-offset-2' },
          }[tier];

          return (
            <div
              className={`mb-6 flex items-center justify-between gap-4 rounded-xl border-2 bg-gradient-to-r px-4 py-3 ${styles.box} ${styles.pulse}`}
              data-testid="campaign-banner"
            >
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white text-lg ${styles.chip.split(' ')[0]}`}>
                  🎉
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900" data-testid="campaign-banner-name">
                    {activeCampaign.name} · %{activeCampaign.discount_percentage} indirim
                  </div>
                  <div className="text-xs text-gray-700">
                    {activeCampaign.applicable_packages && activeCampaign.applicable_packages.length > 0
                      ? `${activeCampaign.applicable_packages.length} planda geçerli`
                      : 'Hiçbir planda geçerli değil'}
                    {' · '}Fiyatlarda otomatik uygulandı
                  </div>
                </div>
              </div>
              {timeLabel && (
                <div className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-bold text-white ${styles.chip}`} data-testid="campaign-countdown">
                  {timeLabel}
                </div>
              )}
            </div>
          );
        })()}

        {/* Trial expired banner */}
        {trialExpired && (
          <div
            data-testid="trial-expired-banner"
            className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4"
          >
            <AlertTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900">
                Deneme süreniz sona ermiştir
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                Hesabınız ve verileriniz korunmaktadır. Hizmete kaldığınız
                yerden devam etmek için aşağıdaki abonelik planlarından birini
                seçin.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Abonelik Planlarınız
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            İşletmeniz için en uygun planı seçin ve hemen başlayın
          </p>
          
          {/* Popular Badge */}
          <div className="flex justify-center mb-6">
            <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium">
              💡 En Popüler: Professional Plan
            </div>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-2" data-testid="billing-cycle-toggle">
            <div className="inline-flex items-center bg-white border border-gray-200 rounded-full p-1 shadow-sm">
              <button
                data-testid="billing-cycle-monthly"
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? 'bg-purple-600 text-white shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Aylık
              </button>
              <button
                data-testid="billing-cycle-yearly"
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  billingCycle === 'yearly'
                    ? 'bg-purple-600 text-white shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yıllık
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  billingCycle === 'yearly' ? 'bg-emerald-400 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  -%20
                </span>
              </button>
            </div>
          </div>
          {billingCycle === 'yearly' && (
            <p className="text-center text-sm text-emerald-600 mb-4" data-testid="yearly-savings-label">
              💚 Yıllık ödeme ile aylık ödemeye göre 2 ay+ tasarruf edersiniz
            </p>
          )}
        </div>

        {/* Pricing Cards — Solo Plan yatay (üstte tek satır), diğer 3 plan
            grid-cols-3 (altta yan yana). Solo tek kullanıcılı çalışanlar
            için fiyatlandırma piramidinin girişidir; ekibi olan işletmeler
            için Starter/Professional/Enterprise kartları grid'de gösterilir. */}
        {(() => {
          const soloEntry = Object.entries(packages).find(([id]) => id === 'solo');
          const teamEntries = Object.entries(packages).filter(([id]) => id !== 'solo');

          const renderMarketingBadge = (packageId, packageData, isSelected = false) => {
            // Kullanıcı seçtiği plana (spotlight'ta olan) "En Popüler"
            // rozeti verilir — packageId="professional" olmasa bile.
            // Ultra Admin marketing_badge ayarladıysa o her zaman kazanır.
            if (packageData.marketing_badge_text || packageData.marketing_badge) {
              return (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div
                    className={`px-4 py-1 rounded-full text-sm font-medium shadow-md whitespace-nowrap ${
                      packageData.marketing_badge === 'limited_time'
                        ? 'bg-red-500 text-white animate-pulse'
                        : packageData.marketing_badge === 'best_value'
                        ? 'bg-emerald-500 text-white'
                        : packageData.marketing_badge === 'new'
                        ? 'bg-blue-500 text-white'
                        : packageData.marketing_badge === 'recommended'
                        ? 'bg-amber-500 text-white'
                        : 'bg-purple-600 text-white'
                    }`}
                    data-testid={`marketing-badge-${packageId}`}
                  >
                    {packageData.marketing_badge_text || (
                      packageData.marketing_badge === 'most_popular' ? '🔥 En Popüler' :
                      packageData.marketing_badge === 'best_value' ? '⭐ En Avantajlı' :
                      packageData.marketing_badge === 'limited_time' ? '⏰ Sınırlı Süre' :
                      packageData.marketing_badge === 'new' ? '🆕 Yeni' :
                      packageData.marketing_badge === 'recommended' ? '👍 Tavsiye Edilen' :
                      ''
                    )}
                  </div>
                </div>
              );
            }
            if (isSelected) {
              return (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium shadow-md whitespace-nowrap animate-pulse"
                       data-testid={`selected-badge-${packageId}`}>
                    ✓ Seçildi
                  </div>
                </div>
              );
            }
            return null;
          };

          // "Sonraki fatura tarihi" — kullanıcı ücretli abonelikteyse
          // `company.subscription_end`, trial'daysa `trial_info.trial_end`
          // gösterilir. Null döner ise kart üzerinde tarih bilgisi çıkmaz.
          const nextBillingLabel = (() => {
            try {
              const trial = user?.trial_info;
              if (trial?.is_trial && trial.trial_end) {
                const d = new Date(trial.trial_end);
                if (!isNaN(d.getTime())) return `Deneme süreniz ${d.toLocaleDateString('tr-TR')} tarihinde biter`;
              }
              const subEnd = user?.company?.subscription_end;
              if (subEnd) {
                const d = new Date(subEnd);
                if (!isNaN(d.getTime())) return `Sonraki fatura tarihi: ${d.toLocaleDateString('tr-TR')}`;
              }
            } catch { /* noop */ }
            return null;
          })();

          // Handler: cancel-at-period-end (non-destructive). Kullanıcı
          // dönem sonuna kadar erişimini korur, otomatik yenileme kapanır.
          const handleCancelSubscription = async () => {
            const conf = window.confirm(
              'Aboneliğinizi iptal etmek istediğinize emin misiniz?\n\n' +
              'Mevcut döneminizin sonuna kadar hizmete erişmeye devam edeceksiniz. ' +
              'Otomatik yenileme kapatılacaktır.'
            );
            if (!conf) return;
            try {
              const res = await axios.post(`${BACKEND_URL}/api/company/cancel-subscription`);
              toast.success(res.data?.message || 'Aboneliğiniz iptal edildi');
              await checkAuth();
            } catch (e) {
              toast.error(e.response?.data?.detail || 'İptal işlemi başarısız');
            }
          };

          const renderSubscribeButtons = (packageId, isCurrent = false) => {
            if (isCurrent) {
              return (
                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    disabled
                    data-testid={`current-plan-button-${packageId}`}
                    className="w-full py-3 font-semibold rounded-lg brand-bg-primary text-white opacity-95 cursor-default flex items-center justify-center gap-2 disabled:opacity-95"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Aboneliğiniz Aktif
                  </Button>
                  {nextBillingLabel && (
                    <p
                      className="text-xs text-gray-600 text-center leading-tight"
                      data-testid={`next-billing-info-${packageId}`}
                    >
                      {nextBillingLabel}
                    </p>
                  )}
                  {/* İptal linki — sadece cancel_pending değilse görünür.
                      Cancel_pending durumunda kullanıcı zaten iptal etmiş; kart
                      "Yenilemez" bilgisini üstte hero banner'ında gösterecek. */}
                  {!isCancelPending ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleCancelSubscription(); }}
                      className="w-full text-xs text-gray-500 hover:text-rose-600 underline underline-offset-2 transition-colors py-1"
                      data-testid={`cancel-subscription-${packageId}`}
                    >
                      Aboneliği iptal et
                    </button>
                  ) : (
                    <p
                      className="text-xs text-rose-600 font-medium text-center"
                      data-testid={`cancel-pending-notice-${packageId}`}
                    >
                      İptal edildi · dönem sonuna kadar aktif
                    </p>
                  )}
                </div>
              );
            }
            return (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Button
                  data-testid={`subscribe-real-${packageId}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Downgrade akışında önce preview modal'ını aç,
                    // Stripe checkout'a doğrudan atlama.
                    if (ctaLabelFor(packageId) === 'Hemen Düşür') {
                      openDowngradePreview(packageId);
                    } else {
                      handleSubscribe(packageId, false);
                    }
                  }}
                  disabled={loading && loadingPackage === packageId}
                  className={`w-full py-3 text-white font-medium rounded-lg transition-all duration-200 ${getButtonColor(packageId)}`}
                >
                  {loading && loadingPackage === packageId ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      İşleniyor...
                    </>
                  ) : ctaLabelFor(packageId)}
                </Button>
                <Button
                  data-testid={`subscribe-mockup-${packageId}`}
                  onClick={(e) => { e.stopPropagation(); handleSubscribe(packageId, true); }}
                  disabled={loading && loadingPackage === `${packageId}-mockup`}
                  variant="outline"
                  className="w-full py-2.5 font-medium rounded-lg border-dashed border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
                >
                  {loading && loadingPackage === `${packageId}-mockup` ? 'Demo İşleniyor...' : 'Demo Ödeme (Test)'}
                </Button>
              </div>
            );
          };

          return (
            <>
              {/* SOLO — yatay banner (üstte, tam genişlik). Tıklandığında
                  diğer kartlar gibi spotlight'a girer. */}
              {soloEntry && (() => {
                const [packageId, packageData] = soloEntry;
                const dp = getDisplayPrice(packageData, packageId);
                // Marketing bullets — Ultra Admin > Paket Düzenle > "Ana Sayfa
                // Özellik Maddeleri" bölümüne bağlı. Boşsa feature-ID listesi
                // FEATURE_BY_ID üzerinden Türkçeye çevrilerek gösterilir.
                const features = resolvePlanFeatures(packageData, {
                  featureIdToName: (id) => FEATURE_BY_ID[id]?.name,
                });
                const isSelected = selectedPlanId === packageId;
                const isCurrent = packageId.toLowerCase() === currentPlanSlug;
                return (
                  <Card
                    key={packageId}
                    onClick={() => setSelectedPlanId(packageId)}
                    className={`relative p-6 md:p-8 mb-6 transition-all duration-300 cursor-pointer ${getPackageColor(packageId)} ${
                      isCurrent
                        ? 'ring-2 brand-border-primary shadow-xl md:scale-[1.02]'
                        : isSelected
                        ? 'ring-2 ring-purple-400 shadow-xl border-purple-300 md:scale-[1.02]'
                        : 'hover:shadow-lg opacity-95'
                    }`}
                    style={isCurrent ? { '--tw-ring-color': 'var(--brand-primary, #7C3AED)' } : undefined}
                    data-testid="solo-plan-horizontal"
                    data-selected={isSelected ? 'true' : 'false'}
                    data-current={isCurrent ? 'true' : 'false'}
                  >
                    {isCurrent && (
                      <div
                        className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full brand-bg-primary text-white shadow-lg flex items-center justify-center ring-4 ring-white"
                        title="Şu anki planınız"
                        data-testid={`current-plan-badge-${packageId}`}
                      >
                        <CheckIcon className="w-5 h-5" strokeWidth={3} />
                      </div>
                    )}
                    {renderMarketingBadge(packageId, packageData, isSelected)}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-center">
                      <div className="md:col-span-2 md:border-r md:pr-8 border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center flex-shrink-0">
                            {getPackageIcon(packageId)}
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900">{packageData.name}</h3>
                            <p className="text-sm text-gray-500">
                              {packageData.user_limit} kullanıcı — tek başına çalışanlar için
                            </p>
                          </div>
                        </div>
                        <div className="mb-4">
                          <div className="flex items-baseline">
                            <span className="text-4xl font-bold text-gray-900">${dp.price}</span>
                            <span className="text-gray-600 ml-2">{dp.suffix}</span>
                          </div>
                          {billingCycle === 'yearly' && (
                            <div className="mt-1 text-xs text-emerald-600 font-medium">
                              <span className="line-through text-gray-400 mr-1">${dp.fullYear}</span>
                              ${dp.savings} tasarruf (%{dp.discountPct}{dp.hasCampaign ? ` + %${dp.campaignPct} kampanya` : ''})
                            </div>
                          )}
                          {billingCycle === 'monthly' && dp.hasCampaign && (
                            <div className="mt-1 text-xs text-rose-600 font-medium" data-testid={`monthly-campaign-savings-${packageId}`}>
                              <span className="line-through text-gray-400 mr-1">${dp.baseMonthly}</span>
                              %{dp.campaignPct} kampanya indirimi
                            </div>
                          )}
                          {dp.hasCampaign && (
                            <div className="campaign-shimmer-badge mt-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700" data-testid={`campaign-badge-${packageId}`}>
                              <span className="relative z-10">🎉 Kampanya %{dp.campaignPct} indirim</span>
                            </div>
                          )}
                          {packageData.active_subscribers > 0 && (
                            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1" data-testid={`subscribers-${packageId}`}>
                              <span className="inline-flex w-2 h-2 bg-emerald-500 rounded-full"></span>
                              <span className="font-semibold text-gray-700">{packageData.active_subscribers}</span> şirket bu paketi seçti
                            </p>
                          )}
                        </div>
                        {renderSubscribeButtons(packageId, isCurrent)}
                      </div>
                      <div className="md:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                          Solo planla yapabilecekleriniz
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                          {features.map((feature, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">
                                {FEATURE_BY_ID[feature]?.name || feature}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                );
              })()}

              {/* TEAM PLANS — Starter/Professional/Enterprise altta 3 sütun.
                  Kullanıcı bir karta tıklarsa selectedPlanId güncellenir,
                  o kart spotlight'a girer (scale + purple ring + En Popüler
                  rozeti). "Hemen Başla"/"Demo" butonları event.stopPropagation
                  ile ayrı çalışır — sadece boş alan tıklaması seçim yapar. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="team-plans-grid">
                {teamEntries.map(([packageId, packageData]) => {
                  const dp = getDisplayPrice(packageData, packageId);
                  const isSelected = selectedPlanId === packageId;
                  const isCurrent = packageId.toLowerCase() === currentPlanSlug;
                  return (
                    <Card
                      key={packageId}
                      onClick={() => setSelectedPlanId(packageId)}
                      className={`relative p-8 text-center transition-all duration-300 cursor-pointer ${getPackageColor(packageId)} ${
                        isCurrent
                          ? 'lg:transform lg:scale-105 ring-2 brand-border-primary shadow-xl'
                          : isSelected
                          ? 'lg:transform lg:scale-105 ring-2 ring-purple-400 shadow-xl border-purple-300'
                          : 'hover:shadow-lg hover:scale-[1.02] opacity-95'
                      }`}
                      style={isCurrent ? { '--tw-ring-color': 'var(--brand-primary, #7C3AED)' } : undefined}
                      data-testid={`plan-card-${packageId}`}
                      data-selected={isSelected ? 'true' : 'false'}
                      data-current={isCurrent ? 'true' : 'false'}
                    >
                      {isCurrent && (
                        <div
                          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full brand-bg-primary text-white shadow-lg flex items-center justify-center ring-4 ring-white"
                          title="Şu anki planınız"
                          data-testid={`current-plan-badge-${packageId}`}
                        >
                          <CheckIcon className="w-5 h-5" strokeWidth={3} />
                        </div>
                      )}
                      {renderMarketingBadge(packageId, packageData, isSelected)}
                      <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                          {getPackageIcon(packageId)}
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{packageData.name}</h3>
                      <div className="mb-6">
                        <div className="flex items-center justify-center">
                          <span className="text-4xl font-bold text-gray-900">${dp.price}</span>
                          <span className="text-gray-600 ml-2">{dp.suffix}</span>
                        </div>
                        {billingCycle === 'yearly' && (
                          <div className="mt-1 text-xs text-emerald-600 font-medium">
                            <span className="line-through text-gray-400 mr-1">${dp.fullYear}</span>
                            ${dp.savings} tasarruf (%{dp.discountPct}{dp.hasCampaign ? ` + %${dp.campaignPct} kampanya` : ''})
                          </div>
                        )}
                        {billingCycle === 'monthly' && dp.hasCampaign && (
                          <div className="mt-1 text-xs text-rose-600 font-medium" data-testid={`monthly-campaign-savings-${packageId}`}>
                            <span className="line-through text-gray-400 mr-1">${dp.baseMonthly}</span>
                            %{dp.campaignPct} kampanya indirimi
                          </div>
                        )}
                        {dp.hasCampaign && (
                          <div className="campaign-shimmer-badge mt-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700" data-testid={`campaign-badge-${packageId}`}>
                            <span className="relative z-10">🎉 Kampanya %{dp.campaignPct} indirim</span>
                          </div>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          {packageData.user_limit} kullanıcıya kadar
                        </p>
                        {packageData.active_subscribers > 0 && (
                          <p className="text-xs text-gray-500 mt-2 flex items-center justify-center gap-1" data-testid={`subscribers-${packageId}`}>
                            <span className="inline-flex w-2 h-2 bg-emerald-500 rounded-full"></span>
                            Bu paketi seçen <span className="font-semibold text-gray-700">{packageData.active_subscribers}</span> şirket
                          </p>
                        )}
                      </div>
                      <ul className="space-y-3 mb-8 text-left">
                        {resolvePlanFeatures(packageData, {
                          featureIdToName: (id) => FEATURE_BY_ID[id]?.name,
                        }).map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {renderSubscribeButtons(packageId, isCurrent)}
                      {packageId === 'starter' && !trialExpired && (
                        <p className="text-xs text-gray-500 mt-3">14 gün ücretsiz deneme</p>
                      )}
                    </Card>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* Feature Comparison Table — Desktop only */}
        {Object.keys(packages).length > 0 && (
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden mb-12" data-testid="feature-comparison-table">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 text-center">
                Hangi planda hangi özellik var?
              </h3>
              <p className="text-center text-gray-500 text-sm mt-1">
                Tüm özelliklerin paket karşılaştırması
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold text-gray-700 w-1/3">Özellik</th>
                    {Object.entries(packages).map(([slug, pkg]) => (
                      <th
                        key={slug}
                        className={`text-center px-4 py-3 font-semibold ${
                          slug === 'professional' ? 'text-purple-700 bg-purple-50' : 'text-gray-700'
                        }`}
                      >
                        <div>{pkg.name}</div>
                        <div className={`text-xs font-normal mt-1 ${
                          slug === 'professional' ? 'text-purple-600' : 'text-gray-500'
                        }`}>
                          ${(() => {
                            const dp2 = getDisplayPrice(pkg, slug);
                            return dp2.price;
                          })()}
                          {billingCycle === 'yearly' ? '/yıl' : '/ay'}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group features by category, preserve catalog order
                    const grouped = {};
                    FEATURE_CATALOG.forEach(f => {
                      if (!grouped[f.category]) grouped[f.category] = [];
                      grouped[f.category].push(f);
                    });
                    const rows = [];
                    Object.entries(grouped).forEach(([category, features]) => {
                      rows.push(
                        <tr key={`cat-${category}`} className="bg-gray-50">
                          <td colSpan={1 + Object.keys(packages).length} className="px-6 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                            {category}
                          </td>
                        </tr>
                      );
                      features.forEach(feat => {
                        rows.push(
                          <tr key={feat.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                            <td className="px-6 py-3 text-gray-800">{feat.name}</td>
                            {Object.entries(packages).map(([slug, pkg]) => {
                              const has = (pkg.features || []).includes(feat.id);
                              return (
                                <td
                                  key={slug}
                                  className={`text-center px-4 py-3 ${
                                    slug === 'professional' ? 'bg-purple-50/50' : ''
                                  }`}
                                  data-testid={`feat-cell-${slug}-${feat.id}`}
                                >
                                  {has ? (
                                    <CheckIcon className="w-5 h-5 text-emerald-500 inline-block" />
                                  ) : (
                                    <XIcon className="w-5 h-5 text-gray-300 inline-block" />
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      });
                    });
                    return rows;
                  })()}
                  {/* Limits row */}
                  <tr className="bg-gray-50">
                    <td colSpan={1 + Object.keys(packages).length} className="px-6 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Limitler
                    </td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-6 py-3 text-gray-800">Kullanıcı Limiti</td>
                    {Object.entries(packages).map(([slug, pkg]) => (
                      <td key={slug} className={`text-center px-4 py-3 font-medium ${
                        slug === 'professional' ? 'bg-purple-50/50 text-purple-700' : 'text-gray-700'
                      }`}>
                        {pkg.user_limit || '—'}
                      </td>
                    ))}
                  </tr>
                  {Object.values(packages).some(p => p.storage_limit_gb) && (
                    <tr className="border-t border-gray-100">
                      <td className="px-6 py-3 text-gray-800">Depolama</td>
                      {Object.entries(packages).map(([slug, pkg]) => (
                        <td key={slug} className={`text-center px-4 py-3 font-medium ${
                          slug === 'professional' ? 'bg-purple-50/50 text-purple-700' : 'text-gray-700'
                        }`}>
                          {pkg.storage_limit_gb ? `${pkg.storage_limit_gb} GB` : '—'}
                        </td>
                      ))}
                    </tr>
                  )}
                  {Object.values(packages).some(p => p.trial_days) && (
                    <tr className="border-t border-gray-100">
                      <td className="px-6 py-3 text-gray-800">Ücretsiz Deneme</td>
                      {Object.entries(packages).map(([slug, pkg]) => (
                        <td key={slug} className={`text-center px-4 py-3 font-medium ${
                          slug === 'professional' ? 'bg-purple-50/50 text-purple-700' : 'text-gray-700'
                        }`}>
                          {pkg.trial_days ? `${pkg.trial_days} gün` : '—'}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FAQ or Additional Info */}
        <div className="bg-white rounded-xl p-8 shadow-sm">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Sıkça Sorulan Sorular
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Planımı değiştirebilir miyim?
              </h4>
              <p className="text-gray-600 text-sm">
                Evet, istediğiniz zaman planınızı yükseltebilir veya düşürebilirsiniz. 
                Değişiklik bir sonraki fatura döneminde geçerli olur.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Ödeme güvenli mi?
              </h4>
              <p className="text-gray-600 text-sm">
                Tüm ödemeler Stripe üzerinden güvenli bir şekilde işlenir. 
                Kredi kartı bilgileriniz bizim sunucularımızda saklanmaz.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                İptal edebilir miyim?
              </h4>
              <p className="text-gray-600 text-sm">
                İstediğiniz zaman aboneliğinizi iptal edebilirsiniz. 
                İptal sonrası mevcut dönem sonuna kadar hizmetten yararlanabilirsiniz.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Destek alabilir miyim?
              </h4>
              <p className="text-gray-600 text-sm">
                Tüm planlarda email desteği mevcuttur. Professional ve Enterprise 
                planlarında öncelikli destek alırsınız.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="text-center mt-8">
          <p className="text-gray-600">
            Sorularınız mı var? 
            <a 
              href="mailto:info@advancecash.io" 
              className="text-purple-600 hover:text-purple-700 font-medium ml-1"
            >
              İletişime geçin
            </a>
          </p>
        </div>
      </div>

      {/* ================================================================
          DOWNGRADE PREVIEW MODAL
          Kullanıcı "Hemen Düşür" butonuna tıkladığında önce burada
          etkilenecek kullanıcı ve kaybedilecek özellikleri görür. Ancak
          onaylarsa gerçek checkout akışı tetiklenir.
          ================================================================ */}
      {downgradePreview.open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={closeDowngradePreview}
          data-testid="downgrade-preview-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            {downgradePreview.loading ? (
              <div className="p-10 text-center text-gray-600">Hesaplanıyor…</div>
            ) : downgradePreview.data ? (() => {
              const d = downgradePreview.data;
              const overLimit = d.users_over_limit || 0;
              const featuresLost = d.features_lost || [];
              return (
                <>
                  <div className="p-6 border-b flex items-start justify-between gap-3">
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mb-2">
                        <ArrowDownIcon className="w-3.5 h-3.5" /> Plan Düşürme
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {d.current_plan.name} → {d.target_plan.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Değişiklik dönem sonunda geçerli olacaktır. Onaylamadan önce
                        aşağıdaki etkileri gözden geçirin.
                      </p>
                    </div>
                    <button
                      onClick={closeDowngradePreview}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      data-testid="downgrade-preview-close"
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* User limit impact */}
                    {overLimit > 0 ? (
                      <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-4" data-testid="downgrade-users-impact">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                            <UsersIcon className="w-5 h-5 text-rose-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-rose-900">
                              {overLimit} kullanıcı devre dışı bırakılacak
                            </div>
                            <div className="text-sm text-rose-700 mt-1">
                              Mevcut plan {d.current_plan.user_limit} kullanıcı destekliyor;
                              hedef plan yalnızca {d.target_plan.user_limit} kullanıcı destekliyor.
                              Şu anda {d.current_user_count} aktif kullanıcınız var.
                            </div>
                            {(d.users_at_risk || []).length > 0 && (
                              <div className="mt-3 rounded-lg bg-white border border-rose-200 divide-y divide-rose-100 max-h-40 overflow-y-auto" data-testid="downgrade-at-risk-list">
                                {(d.users_at_risk || []).map((u) => (
                                  <div key={u.id} className="px-3 py-2 flex items-center justify-between text-sm">
                                    <div>
                                      <div className="font-medium text-gray-900">{u.full_name}</div>
                                      <div className="text-xs text-gray-500">{u.email}</div>
                                    </div>
                                    {u.role_name && (
                                      <span className="text-xs text-gray-500">{u.role_name}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-rose-800 mt-3">
                              Öneri: Downgrade&apos;den önce bu kullanıcıları
                              Ayarlar &gt; Kullanıcı Yönetimi&apos;nden devre dışı bırakın.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex items-center gap-3">
                          <CheckIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <div className="text-sm text-emerald-800">
                            <strong>{d.current_user_count} kullanıcınız</strong> hedef plan limitine
                            (<strong>{d.target_plan.user_limit}</strong>) sığıyor — hiçbir hesap devre dışı bırakılmayacak.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Features lost */}
                    {featuresLost.length > 0 && (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4" data-testid="downgrade-features-lost">
                        <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <AlertTriangleIcon className="w-4 h-4 text-amber-500" />
                          Kaybedilecek {featuresLost.length} özellik
                        </div>
                        <ul className="text-sm text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                          {featuresLost.map((f) => (
                            <li key={f} className="flex items-start gap-2">
                              <XIcon className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
                              <span>{FEATURE_BY_ID[f]?.name || f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Price change */}
                    <div className="rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                      <div className="text-sm text-gray-600">Aylık tutar</div>
                      <div className="text-right">
                        <span className="line-through text-gray-400 mr-2">${d.current_plan.price}</span>
                        <span className="text-lg font-bold text-emerald-600">${d.target_plan.price}</span>
                        <span className="text-xs text-gray-500 ml-1">/ay</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-2">
                    <Button variant="outline" onClick={closeDowngradePreview} data-testid="downgrade-cancel">
                      Vazgeç
                    </Button>
                    <Button
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => {
                        const slug = downgradePreview.targetSlug;
                        closeDowngradePreview();
                        handleSubscribe(slug, false);
                      }}
                      data-testid="downgrade-confirm"
                    >
                      Anladım, Planı Düşür
                    </Button>
                  </div>
                </>
              );
            })() : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;